import { useCallback, useEffect, useRef } from 'react';
import {
  connectInventorySocket,
  disconnectInventorySocket,
  INVENTORY_COUNT_UPDATED_EVENT,
  INVENTORY_JOIN_EVENT,
  INVENTORY_LEAVE_EVENT,
  INVENTORY_POLL_INTERVAL_MS,
  type InventoryCountUpdatedEvent,
  type InventoryJoinPayload,
} from '@/realtime/inventorySocket';

type UseInventoryRoomSyncOptions = {
  enabled: boolean;
  join: InventoryJoinPayload | null;
  currentUserId?: number | null;
  onRemoteUpdate: (event: InventoryCountUpdatedEvent) => void;
  poll: () => void | Promise<void>;
  pollIntervalMs?: number;
};

/** Socket.io room subscription + 10s poll fallback for multi-user inventory screens. */
export function useInventoryRoomSync({
  enabled,
  join,
  currentUserId,
  onRemoteUpdate,
  poll,
  pollIntervalMs = INVENTORY_POLL_INTERVAL_MS,
}: UseInventoryRoomSyncOptions): void {
  const onRemoteRef = useRef(onRemoteUpdate);
  onRemoteRef.current = onRemoteUpdate;
  const pollRef = useRef(poll);
  pollRef.current = poll;

  const runPoll = useCallback(async () => {
    try {
      await pollRef.current();
    } catch {
      // parent handles errors
    }
  }, []);

  useEffect(() => {
    if (!enabled || join == null || join.locationId <= 0) {
      return;
    }

    const token = window.localStorage.getItem('jwt');
    if (!token) {
      return;
    }

    const socket = connectInventorySocket(token);
    const joinPayload = join;

    const handleRemote = (event: InventoryCountUpdatedEvent) => {
      if (currentUserId != null && event.updatedByUserId === currentUserId) {
        return;
      }
      onRemoteRef.current(event);
    };

    const doJoin = () => {
      socket.emit(INVENTORY_JOIN_EVENT, joinPayload);
    };

    socket.on('connect', doJoin);
    socket.on(INVENTORY_COUNT_UPDATED_EVENT, handleRemote);
    if (socket.connected) {
      doJoin();
    }

    void runPoll();
    const intervalId = window.setInterval(() => {
      void runPoll();
    }, pollIntervalMs);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void runPoll();
        if (socket.connected) {
          doJoin();
        }
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
      socket.off('connect', doJoin);
      socket.off(INVENTORY_COUNT_UPDATED_EVENT, handleRemote);
      socket.emit(INVENTORY_LEAVE_EVENT, joinPayload);
    };
  }, [
    enabled,
    join?.scope,
    join?.locationId,
    join?.vendingMachineId,
    join?.storageSiteId,
    currentUserId,
    pollIntervalMs,
    runPoll,
  ]);

  useEffect(() => {
    return () => {
      if (!enabled) {
        disconnectInventorySocket();
      }
    };
  }, [enabled]);
}

/** True when local qty matches last saved (user is not mid-edit). */
export function rowIsCleanForRemoteMerge(draft: number, lastSaved: number | undefined): boolean {
  const saved = lastSaved ?? draft;
  return draft === saved;
}
